// The save side of the prep editor. It never holds the text: the editor's own
// elements do, and `read` fetches the current value when a save goes out. What
// it holds is which sections are dirty, a version for each edit so a save that
// finishes while the person keeps typing does not clear the newer edit, and the
// retry. A failed save leaves everything dirty and tries again, so nothing typed
// is ever dropped.
//
//  - A section's html and title go out 600 ms after the last keystroke in it.
//  - Blur, tab hide, and page hide send everything now (the last with keepalive).
//  - Order and deleted ids go out at once; `deleted` is sent only when this
//    session changed it, since a post replaces the server's list.
//  - One request at a time, except at page hide.

import type { PrepSave } from "./prepApi";

export type SaveStatus = "idle" | "saving" | "saved" | "failed";

export interface SaverDeps {
  readonly slug: string;
  readonly save: (req: PrepSave, opts: { keepalive?: boolean }) => Promise<unknown>;
  /** The editor's current title and html for a section, or null if it is gone. */
  readonly read: (id: string) => { title: string; html: string } | null;
  readonly order: () => string[];
  readonly deleted: () => string[];
}

export const SAVE_DELAY_MS = 600;
const RETRY_MS = [1000, 2000, 4000, 8000, 15000];

export class PrepSaver {
  status: SaveStatus = "idle";
  /** Counts up on every finished save, so a poll can tell it started before one. */
  tick = 0;

  private readonly deps: SaverDeps;
  private readonly base = new Map<string, { title: string; html: string }>();
  private readonly dirtyHtml = new Map<string, number>();
  private readonly dirtyTitle = new Map<string, number>();
  private readonly savedTick = new Map<string, number>();
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly ready = new Set<string>();
  private orderVer = 0;
  private orderSentVer = 0;
  private deletedVer = 0;
  private deletedSentVer = 0;
  private layoutTick = 0;
  private version = 0;
  private inflight = 0;
  private fails = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  private readonly listeners = new Set<() => void>();

  constructor(deps: SaverDeps) {
    this.deps = deps;
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** The server's values for a section, as loaded or last saved. */
  setBase(id: string, title: string, html: string): void {
    this.base.set(id, { title, html });
  }

  getBase(id: string): { title: string; html: string } | undefined {
    return this.base.get(id);
  }

  dropBase(id: string): void {
    this.base.delete(id);
    this.dirtyHtml.delete(id);
    this.dirtyTitle.delete(id);
    this.ready.delete(id);
    const t = this.timers.get(id);
    if (t) clearTimeout(t);
    this.timers.delete(id);
  }

  hasBase(id: string): boolean {
    return this.base.has(id);
  }

  /** A section being typed in or saved: a poll must not overwrite it. */
  busy(id: string, since: number): boolean {
    return this.dirtyHtml.has(id) || this.dirtyTitle.has(id) || (this.savedTick.get(id) ?? 0) > since;
  }

  /** The order or the deleted list is changing, or changed since `since`. */
  layoutBusy(since: number): boolean {
    return this.orderVer !== this.orderSentVer || this.deletedVer !== this.deletedSentVer || this.layoutTick > since;
  }

  get dirty(): boolean {
    return this.dirtyHtml.size > 0 || this.dirtyTitle.size > 0 || this.orderVer !== this.orderSentVer || this.deletedVer !== this.deletedSentVer;
  }

  touchHtml(id: string): void {
    this.dirtyHtml.set(id, ++this.version);
    this.arm(id);
  }

  touchTitle(id: string): void {
    this.dirtyTitle.set(id, ++this.version);
    this.arm(id);
  }

  /** A section made here: its text, title, and place all go up. */
  touchNew(id: string): void {
    this.dirtyHtml.set(id, ++this.version);
    this.dirtyTitle.set(id, ++this.version);
    this.ready.add(id);
    this.touchOrder();
  }

  touchOrder(): void {
    this.orderVer = ++this.version;
    this.pump();
    this.emit();
  }

  touchDeleted(): void {
    this.deletedVer = ++this.version;
    this.touchOrder();
  }

  /** Send everything that is waiting, now. */
  flush(keepalive = false): void {
    for (const id of this.dirtyHtml.keys()) this.ready.add(id);
    for (const id of this.dirtyTitle.keys()) this.ready.add(id);
    for (const t of this.timers.values()) clearTimeout(t);
    this.timers.clear();
    this.pump(keepalive);
  }

  /** Undoes a dispose: a strict-mode remount reuses the same saver. */
  revive(): void {
    this.disposed = false;
  }

  dispose(): void {
    this.disposed = true;
    for (const t of this.timers.values()) clearTimeout(t);
    this.timers.clear();
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.listeners.clear();
  }

  private arm(id: string): void {
    const old = this.timers.get(id);
    if (old) clearTimeout(old);
    this.timers.set(
      id,
      setTimeout(() => {
        this.timers.delete(id);
        this.ready.add(id);
        this.pump();
      }, SAVE_DELAY_MS),
    );
    this.emit();
  }

  private set(status: SaveStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.emit();
  }

  private emit(): void {
    if (this.disposed) return;
    for (const fn of this.listeners) fn();
  }

  private pump(keepalive = false): void {
    if (this.disposed && !keepalive) return;
    if (this.inflight > 0 && !keepalive) return;
    if (this.retryTimer && !keepalive) return;

    const sections: Record<string, string> = {};
    const titles: Record<string, string> = {};
    const sentHtml = new Map<string, number>();
    const sentTitle = new Map<string, number>();
    const sentValues = new Map<string, { title: string; html: string }>();

    for (const id of this.ready) {
      const cur = this.deps.read(id);
      const wantHtml = this.dirtyHtml.get(id);
      const wantTitle = this.dirtyTitle.get(id);
      if (!cur) {
        this.dirtyHtml.delete(id);
        this.dirtyTitle.delete(id);
        this.ready.delete(id);
        continue;
      }
      const base = this.base.get(id);
      if (wantHtml !== undefined) {
        if (!base || base.html !== cur.html) {
          sections[id] = cur.html;
          sentHtml.set(id, wantHtml);
        } else this.dirtyHtml.delete(id);
      }
      if (wantTitle !== undefined) {
        if (!base || base.title !== cur.title) {
          titles[id] = cur.title;
          sentTitle.set(id, wantTitle);
        } else this.dirtyTitle.delete(id);
      }
      sentValues.set(id, cur);
    }

    const sendOrder = this.orderVer !== this.orderSentVer;
    const sendDeleted = this.deletedVer !== this.deletedSentVer;
    const orderVer = this.orderVer;
    const deletedVer = this.deletedVer;
    const hasText = Object.keys(sections).length > 0 || Object.keys(titles).length > 0;

    if (!hasText && !sendOrder && !sendDeleted) {
      this.ready.clear();
      if (!this.dirty) this.set(this.status === "idle" ? "idle" : "saved");
      else this.emit();
      return;
    }

    const layout: { order?: string[]; titles?: Record<string, string>; deleted?: string[] } = {};
    if (sendOrder) layout.order = this.deps.order();
    if (Object.keys(titles).length > 0) layout.titles = titles;
    if (sendDeleted) layout.deleted = this.deps.deleted();
    const req: PrepSave = {
      slug: this.deps.slug,
      ...(Object.keys(sections).length > 0 ? { sections } : {}),
      ...(Object.keys(layout).length > 0 ? { layout } : {}),
    };

    for (const id of sentValues.keys()) this.ready.delete(id);
    this.inflight++;
    this.set("saving");
    this.deps
      .save(req, { keepalive })
      .then(() => {
        this.inflight--;
        this.fails = 0;
        const done = ++this.tick;
        for (const [id, v] of sentHtml) {
          const cur = sentValues.get(id);
          const b = this.base.get(id);
          if (cur) this.base.set(id, { title: b?.title ?? cur.title, html: cur.html });
          if (this.dirtyHtml.get(id) === v) this.dirtyHtml.delete(id);
          this.savedTick.set(id, done);
        }
        for (const [id, v] of sentTitle) {
          const cur = sentValues.get(id);
          const b = this.base.get(id);
          if (cur) this.base.set(id, { title: cur.title, html: b?.html ?? cur.html });
          if (this.dirtyTitle.get(id) === v) this.dirtyTitle.delete(id);
          this.savedTick.set(id, done);
        }
        if (sendOrder) {
          this.orderSentVer = orderVer;
          this.layoutTick = done;
        }
        if (sendDeleted) {
          this.deletedSentVer = deletedVer;
          this.layoutTick = done;
        }
        if (this.dirty || this.ready.size > 0) {
          this.set("saving");
          this.pump();
        } else this.set("saved");
      })
      .catch(() => {
        this.inflight--;
        // Put back what was taken, so the retry sends it.
        for (const id of sentValues.keys()) if (this.dirtyHtml.has(id) || this.dirtyTitle.has(id)) this.ready.add(id);
        this.set("failed");
        if (this.disposed || keepalive) return;
        const wait = RETRY_MS[Math.min(this.fails, RETRY_MS.length - 1)] ?? 15000;
        this.fails++;
        if (this.retryTimer) clearTimeout(this.retryTimer);
        this.retryTimer = setTimeout(() => {
          this.retryTimer = null;
          for (const id of this.dirtyHtml.keys()) this.ready.add(id);
          for (const id of this.dirtyTitle.keys()) this.ready.add(id);
          this.pump();
        }, wait);
      });
  }
}
